import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { connectAuthPost, connectUploadImage, getConnectToken, getConnectUser, clearConnectSession } from './connectApi';
import ProfileLink from './ProfileLink';
import ComposerImage from './ComposerImage';
import NotificationsBell from './NotificationsBell';
import ConnectNav from './ConnectNav';
import './styles/ConnectMessages.css';

const CONVERSATIONS_POLL = 10 * 1000;
const MESSAGES_POLL = 4 * 1000;

export default function ConnectMessages() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  // Leaving a group chat is one tap away, so it asks first
  const [leavingChat, setLeavingChat] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [draftUser, setDraftUser] = useState(null); // chat without a conversation yet
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [notice, setNotice] = useState('');
  const [showMobileNav, setShowMobileNav] = useState(false);

  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const selectedIdRef = useRef(null);

  const formatTime = (timestamp) => {
    const diff = Date.now() - timestamp * 1000;
    if (diff < 1000 * 60) return 'now';
    if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))}m ago`;
    if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))}h ago`;
    return `${Math.floor(diff / (1000 * 60 * 60 * 24))}d ago`;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = useCallback(async () => {
    try {
      const result = await connectAuthPost('connect/conversations/list');
      if (result.success) {
        setConversations(result.data.conversations);
        return result.data.conversations;
      }
    } catch (err) {
      // Non-critical, retried by the poll
    }
    return null;
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const result = await connectAuthPost('connect/messages/contacts');
      if (result.success) {
        setContacts(result.data.contacts);
        return result.data.contacts;
      }
    } catch (err) {
      // Non-critical
    }
    return [];
  }, []);

  // Initial load + honor ?user=ID (Message button on a profile)
  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setUser(getConnectUser());

    const init = async () => {
      const convs = await loadConversations();
      // A meetup chat is opened by conversation id rather than by person.
      const convId = parseInt(searchParams.get('conversation') || '', 10);
      if (convId) {
        const found = (convs || []).find(c => c.id === convId);
        if (found) openConversation(found);
        setSearchParams({}, { replace: true });
        return;
      }

      const targetId = parseInt(searchParams.get('user') || '', 10);
      if (!targetId) return;

      // Existing conversation with that member?
      const existing = (convs || []).find(c => c.other_user.id === targetId);
      if (existing) {
        openConversation(existing);
      } else {
        const contactList = await loadContacts();
        const contact = contactList.find(c => c.id === targetId);
        if (contact) {
          setDraftUser(contact);
          setSelectedConversation(null);
        } else {
          setNotice('You can only message members who follow each other. Follow them and have them follow you back first.');
        }
      }
      // Clean the URL
      setSearchParams({}, { replace: true });
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  // Poll conversation list
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) loadConversations();
    }, CONVERSATIONS_POLL);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Load + poll messages for the open conversation
  useEffect(() => {
    selectedIdRef.current = selectedConversation?.id || null;
    lastMessageIdRef.current = 0;
    setMessages([]);
    if (!selectedConversation) return;

    let cancelled = false;

    const fetchMessages = async (initial) => {
      try {
        const result = await connectAuthPost('connect/messages/list', {
          conversation_id: selectedConversation.id,
          after_id: initial ? 0 : lastMessageIdRef.current
        });
        if (cancelled || !result.success) return;
        const incoming = result.data.messages;
        if (incoming.length > 0) {
          lastMessageIdRef.current = incoming[incoming.length - 1].id;
          setMessages(prev => (initial ? incoming : [...prev, ...incoming]));
          setTimeout(scrollToBottom, 50);
          // Opening/receiving clears the unread badge locally
          setConversations(prev => prev.map(c =>
            c.id === selectedConversation.id ? { ...c, unread: 0 } : c
          ));
        }
      } catch (err) {
        // Retried by the poll
      }
    };

    fetchMessages(true);
    const interval = setInterval(() => {
      if (!document.hidden) fetchMessages(false);
    }, MESSAGES_POLL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedConversation]);

  const openConversation = (conv) => {
    setDraftUser(null);
    setNotice('');
    setSelectedConversation(conv);
  };

  const handleSendMessage = async () => {
    const content = messageInput.trim();
    if ((!content && !imageFile) || isSending) return;
    setIsSending(true);
    try {
      let image = '';
      if (imageFile) {
        const upload = await connectUploadImage(imageFile, 'message');
        if (!upload.success) {
          setNotice(upload.response || 'Could not upload the photo.');
          return;
        }
        image = upload.data.url;
      }

      const payload = selectedConversation
        ? { conversation_id: selectedConversation.id, content, image }
        : { user_id: draftUser.id, content, image };

      const result = await connectAuthPost('connect/messages/send', payload);
      if (!result.success) {
        setNotice(result.response || 'Could not send the message.');
        return;
      }

      setMessageInput('');
      setImageFile(null);
      setNotice('');

      if (selectedConversation) {
        setMessages(prev => [...prev, result.data.message]);
        lastMessageIdRef.current = result.data.message.id;
        setTimeout(scrollToBottom, 50);
        loadConversations();
      } else {
        // First message created the conversation — switch to it
        const convs = await loadConversations();
        const created = (convs || []).find(c => c.id === result.data.conversation_id);
        setDraftUser(null);
        if (created) openConversation(created);
      }
    } catch (err) {
      setNotice('Could not reach the server. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenNewChat = async () => {
    setShowNewChatModal(true);
    setSearchQuery('');
    await loadContacts();
  };

  const handleStartNewChat = (contact) => {
    setShowNewChatModal(false);
    setSearchQuery('');
    const existing = conversations.find(c => c.other_user.id === contact.id);
    if (existing) {
      openConversation(existing);
    } else {
      setSelectedConversation(null);
      setNotice('');
      setDraftUser(contact);
    }
  };

  const handleLogout = async () => {
    try {
      await connectAuthPost('connect/auth/logout');
    } catch (err) {
      // Best-effort — clear locally regardless
    }
    clearConnectSession();
    navigate('/connect');
  };

  const filteredContacts = contacts.filter(c =>
    c.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.current_location || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLeaveChat = async () => {
    if (!selectedConversation || isLeaving) return;
    setIsLeaving(true);
    setLeaveError('');
    try {
      const result = await connectAuthPost('connect/meetups/chat/leave', {
        conversation_id: selectedConversation.id,
      });
      if (result.success) {
        setLeavingChat(false);
        setSelectedConversation(null);
        setMessages([]);
        await loadConversations();
      } else {
        // The API answers 200 with success:false; saying nothing here made a
        // refusal look like a dead button.
        setLeaveError(result.response || 'Could not leave this chat.');
      }
    } catch (err) {
      setLeaveError('Could not reach the server.');
    } finally {
      setIsLeaving(false);
    }
  };

  const isMeetupChat = selectedConversation?.type === 'meetup';
  const chatPartner = selectedConversation?.other_user || draftUser;
  const chatOpen = !!(selectedConversation || draftUser) && (isMeetupChat || !!chatPartner);

  if (!user) return null;

  return (
    <div className="connect-messages-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <ConnectNav active="messages" />
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>
            Log Out
          </button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <ConnectNav active="messages" variant="mobile" onNavigate={() => setShowMobileNav(false)} />
            </div>
            <div className="connect-mobile-nav-footer">
              <Link to="/community-guidelines" className="connect-guidelines-link">
                Community Guidelines
              </Link>
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      {/* Messages Container */}
      <div className="connect-messages-container">
        {/* Conversations List */}
        <div className="connect-conversations-panel">
          <div className="connect-conversations-header">
            <h2>Messages</h2>
            <button
              className="connect-new-message-btn"
              title="New message"
              onClick={handleOpenNewChat}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <line x1="12" y1="8" x2="12" y2="14"/>
                <line x1="9" y1="11" x2="15" y2="11"/>
              </svg>
            </button>
          </div>

          <div className="connect-conversations-list">
            {conversations.length === 0 && !draftUser && (
              <div className="connect-new-chat-empty">
                <p>No conversations yet. Start one with the + button — you can message members who follow each other.</p>
              </div>
            )}
            {draftUser && (
              <div className="connect-conversation-item active">
                <div className="connect-conv-avatar">
                  {draftUser.profile_picture ? (
                    <img src={draftUser.profile_picture} alt={draftUser.full_name} />
                  ) : (
                    <div className="connect-conv-avatar-placeholder">
                      {draftUser.full_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="connect-conv-info">
                  <div className="connect-conv-header">
                    <span className="connect-conv-name">{draftUser.full_name}</span>
                  </div>
                  <p className="connect-conv-preview">New conversation</p>
                </div>
              </div>
            )}
            {conversations.map(conv => (
              <div
                key={conv.id}
                className={`connect-conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''} ${conv.unread > 0 ? 'unread' : ''}`}
                onClick={() => openConversation(conv)}
              >
                <div className="connect-conv-avatar">
                  {conv.type === 'meetup' ? (
                    <div className="connect-conv-avatar-placeholder">⚑</div>
                  ) : conv.other_user.profile_picture ? (
                    <img src={conv.other_user.profile_picture} alt={conv.other_user.full_name} />
                  ) : (
                    <div className="connect-conv-avatar-placeholder">
                      {conv.other_user.full_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="connect-conv-info">
                  <div className="connect-conv-header">
                    <span className="connect-conv-name">
                      {conv.type === 'meetup' ? conv.title : conv.other_user.full_name}
                    </span>
                    <span className="connect-conv-time">{formatTime(conv.timestamp_last)}</span>
                  </div>
                  <p className="connect-conv-preview">
                    {conv.last_is_own ? 'You: ' : ''}{conv.last_message || 'New conversation'}
                  </p>
                </div>
                {conv.unread > 0 && (
                  <span className="connect-unread-badge">{conv.unread}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Chat Panel */}
        <div className={`connect-chat-panel ${chatOpen ? 'active' : ''}`}>
          {chatOpen ? (
            <>
              {/* Chat Header */}
              <div className="connect-chat-header">
                <button
                  className="connect-chat-back-btn"
                  onClick={() => { setSelectedConversation(null); setDraftUser(null); }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div className="connect-chat-user-info">
                  <div className="connect-chat-avatar">
                    {isMeetupChat ? (
                      <div className="connect-chat-avatar-placeholder">⚑</div>
                    ) : (
                      <ProfileLink userId={chatPartner.id} avatar>
                        {chatPartner.profile_picture ? (
                          <img src={chatPartner.profile_picture} alt={chatPartner.full_name} />
                        ) : (
                          <div className="connect-chat-avatar-placeholder">
                            {chatPartner.full_name.charAt(0)}
                          </div>
                        )}
                      </ProfileLink>
                    )}
                  </div>
                  <div>
                    <h3>{isMeetupChat ? selectedConversation.title : <ProfileLink userId={chatPartner.id}>{chatPartner.full_name}</ProfileLink>}</h3>
                    <span className="connect-chat-status">
                      {isMeetupChat
                        ? 'Meetup chat'
                        : [chatPartner.pronouns, chatPartner.current_location].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                </div>
                <div className="connect-chat-actions">
                  {isMeetupChat && selectedConversation.can_leave !== false && (
                    <button
                      title="Leave this group chat"
                      onClick={() => { setLeaveError(''); setLeavingChat(true); }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                        <polyline points="16 17 21 12 16 7"/>
                        <line x1="21" y1="12" x2="9" y2="12"/>
                      </svg>
                    </button>
                  )}
                  {!isMeetupChat && (
                    <button
                      title="View profile"
                      onClick={() => navigate('/connect/user/' + chatPartner.id)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="connect-chat-messages">
                {draftUser && messages.length === 0 && (
                  <div className="connect-new-chat-empty">
                    <p>Say hi to {draftUser.full_name}! 👋</p>
                  </div>
                )}
                {messages.map((msg, i) => {
                  // In a group chat you cannot tell who is talking from the
                  // bubble alone, so incoming messages carry a face and a
                  // name — shown once per run, not on every line.
                  const prev = messages[i - 1];
                  const next = messages[i + 1];
                  const sameAs = (a, b) => a && b && a.sender_id === b.sender_id && a.is_own === b.is_own;
                  const startsRun = !sameAs(prev, msg);
                  // The face sits beside the LAST bubble of a run, where the
                  // tail is, while the name introduces the first one.
                  const endsRun = !sameAs(next, msg);
                  const showSender = isMeetupChat && !msg.is_own && startsRun;
                  return (
                  <div
                    key={msg.id}
                    className={`connect-message ${msg.is_own ? 'sent' : 'received'}${isMeetupChat ? ' group' : ''}${startsRun ? '' : ' continued'}`}
                  >
                    {isMeetupChat && !msg.is_own && (
                      <div className={`connect-message-avatar${endsRun ? '' : ' is-spacer'}`}>
                        {endsRun && (
                          <ProfileLink userId={msg.sender_id} avatar>
                            {msg.sender_picture
                              ? <img src={msg.sender_picture} alt={msg.sender_name} />
                              : <span>{(msg.sender_name || '?').charAt(0).toUpperCase()}</span>}
                          </ProfileLink>
                        )}
                      </div>
                    )}
                    <div className="connect-message-bubble">
                      {showSender && (
                        <span className="connect-message-sender"><ProfileLink userId={msg.sender_id}>{msg.sender_name}</ProfileLink></span>
                      )}
                      {msg.image && (
                        <a href={msg.image} target="_blank" rel="noopener noreferrer" className="connect-message-image">
                          <img src={msg.image} alt="Sent in chat" onLoad={scrollToBottom} />
                        </a>
                      )}
                      {msg.content && <p>{msg.content}</p>}
                      <span className="connect-message-time">{formatTime(msg.timestamp)}</span>
                    </div>
                  </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Notice */}
              {notice && (
                <div className="connect-chat-notice">{notice}</div>
              )}

              {/* Message Input */}
              {imageFile && (
                <div className="connect-chat-image-preview">
                  <ComposerImage file={imageFile} onClear={() => setImageFile(null)} />
                </div>
              )}
              <div className="connect-chat-input">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    e.target.value = '';
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) {
                      setNotice('That photo is over the 10MB limit.');
                      return;
                    }
                    setNotice('');
                    setImageFile(file);
                  }}
                />
                <button
                  type="button"
                  className="connect-attach-btn"
                  title="Send a photo"
                  aria-label="Send a photo"
                  onClick={() => imageInputRef.current.click()}
                  disabled={isSending}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </button>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  maxLength={2000}
                />
                <button
                  className="connect-send-btn"
                  onClick={handleSendMessage}
                  disabled={isSending || (!messageInput.trim() && !imageFile)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="connect-chat-empty">
              <div className="connect-chat-empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3>Your Messages</h3>
              <p>Select a conversation to start chatting</p>
              {notice && <p className="connect-chat-notice-inline">{notice}</p>}
              <Link to="/connect/discover" className="connect-find-people-btn">
                Find People to Connect
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="connect-new-chat-overlay" onClick={() => setShowNewChatModal(false)}>
          <div className="connect-new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="connect-new-chat-header">
              <h3>New Message</h3>
              <button className="connect-new-chat-close" onClick={() => setShowNewChatModal(false)}>
                ×
              </button>
            </div>

            <div className="connect-new-chat-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search people..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="connect-new-chat-users">
              {filteredContacts.length > 0 ? (
                filteredContacts.map(person => (
                  <div
                    key={person.id}
                    className="connect-new-chat-user"
                    onClick={() => handleStartNewChat(person)}
                  >
                    <div className="connect-new-chat-avatar">
                      {person.profile_picture ? (
                        <img src={person.profile_picture} alt={person.full_name} />
                      ) : (
                        <div className="connect-new-chat-avatar-placeholder">
                          {person.full_name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="connect-new-chat-info">
                      <span className="connect-new-chat-name">{person.full_name}</span>
                      <span className="connect-new-chat-details">
                        {[person.pronouns, person.current_location].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                ))
              ) : (
                <div className="connect-new-chat-empty">
                  <p>
                    {contacts.length === 0
                      ? 'No contacts yet. You can message members who follow each other — follow people in Discover first.'
                      : 'No people match your search'}
                  </p>
                </div>
              )}
            </div>

            <div className="connect-new-chat-footer">
              <Link to="/connect/discover" className="connect-discover-link" onClick={() => setShowNewChatModal(false)}>
                Discover more people →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Leaving a group chat is quiet but not trivial, so it is confirmed */}
      {leavingChat && selectedConversation && (
        <div
          className="connect-leave-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setLeavingChat(false); }}
        >
          <div className="connect-leave-dialog" role="dialog" aria-modal="true" aria-labelledby="leave-chat-title">
            <h3 id="leave-chat-title">Leave this chat?</h3>
            <p>
              You will stop receiving messages from <strong>{selectedConversation.title}</strong>,
              but you are still going to the meetup. You can rejoin the chat from
              the meetup page.
            </p>
            {leaveError && <p className="connect-leave-error">{leaveError}</p>}

            <div className="connect-leave-actions">
              <button
                type="button"
                className="connect-leave-cancel"
                onClick={() => setLeavingChat(false)}
                disabled={isLeaving}
              >
                Stay
              </button>
              <button
                type="button"
                className="connect-leave-confirm"
                onClick={handleLeaveChat}
                disabled={isLeaving}
              >
                {isLeaving ? 'Leaving...' : 'Leave chat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
